import { REQUEST_TIMEOUT } from "$root/api/timeouts";
import { isTimeoutError, withTimeout } from "$root/utils";

/**
 * The registry of writes the app is holding but has not sent yet, and the one way to make them land.
 *
 * A user-scoped write is attributed to an identity when its request is **sent**: `auth-interceptors`
 * stamps `X-Impersonate-Email` from `impersonateEmail$` and the bearer from `accessToken$` as the
 * request goes out. Two things hold values back across that moment — a trailing debounce (an AG Grid
 * layout waits a second for the drag to settle, see `usePersistColState`) and the serial queue behind
 * `userStorage` — so a write still in the pipe when the identity moves is delivered as the new user.
 *
 * Hence the rule: **an identity boundary waits for the writes of the identity it is leaving.** Every
 * crossing — impersonation in, impersonation out, an interactive logout — calls `settlePendingWrites`
 * while the outgoing identity's credentials are still the current ones. A session whose credential is
 * already gone is the exception: nothing is left to authenticate those writes with.
 */
export type PendingWrite = {
	/**
	 * Hand whatever is being held to its transport, now. Called for every registered writer before
	 * any `drain` starts, so a debounce that has not fired yet still gets into the queue it drains.
	 */
	flush?(): void;
	/**
	 * Resolve once what the transport was given has gone out — or failed trying.
	 *
	 * `signal` aborts when the wait below runs out of patience. Take it into whatever carries the
	 * write and the deadline cancels it, so a write that could not leave in time is dropped rather
	 * than delivered under the identity that arrives next — which is the misattribution this whole
	 * registry exists to prevent, reappearing at the end of the wait instead of the start.
	 */
	drain?(signal: AbortSignal): Promise<void>;
};

const pendingWrites = new Set<PendingWrite>();

/** Register a holder of unsent writes. Returns the unregister function, so it fits a `useEffect`. */
export function registerPendingWrite(write: PendingWrite): () => void {
	pendingWrites.add(write);
	return () => {
		pendingWrites.delete(write);
	};
}

/**
 * Flush every registered writer, then wait for what they sent to land. Never rejects: a writer that
 * cannot flush, or a request that fails, must not be able to hold an identity change hostage — each
 * one already reports or swallows its own failure.
 *
 * The wait is bounded by `REQUEST_TIMEOUT`, the same allowance every other request in the app gets:
 * a write is waited on for exactly as long as its transport is allowed to spend sending it, so the
 * boundary never abandons a write the request layer would still have delivered. Reaching the bound
 * means a writer is hung on something other than a request, and the bound cancels rather than merely
 * stops waiting — a write released at the deadline goes out under whichever identity arrives next,
 * so at the deadline every writer is told to give up and the write is dropped, which for a column
 * width is the right way to lose.
 */
export async function settlePendingWrites(opts?: { timeout?: number }): Promise<void> {
	const writes = [...pendingWrites];

	for (const write of writes) {
		try {
			write.flush?.();
		} catch {
			// Nothing to do with it here, and the next writer still deserves its flush.
		}
	}

	try {
		await withTimeout(
			(signal) => Promise.allSettled(writes.map(async (write) => write.drain?.(signal))),
			opts?.timeout ?? REQUEST_TIMEOUT,
		);
	} catch (err) {
		if (!isTimeoutError(err)) {
			throw err;
		}
		// The deadline is the documented outcome, not a fault: the signal has told every writer to give
		// up, and the caller has an identity to move.
	}
}
